import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp,
  type DocumentReference,
} from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

import { db } from "integrations/firebase/firestore";

/**
 * Finding somebody who is already on the app, from the event's Attendees tab.
 *
 * Organisers cannot read `users`, so the search is the backend's. The organiser
 * files `events/{eventId}/attendee_lookups/{id}` with the typed text; the
 * trigger checks that they organise this event and answers on the same
 * document (functions/python/app/application/service/attendee_lookup.py).
 * Only the person who asked can read the answer, and it holds at most eight
 * people, each with a name, a company, an email address and an id.
 *
 * Picking one does not write anything here. The form fills in the person's
 * details and is submitted as the ordinary ADD_EVENT_ATTENDEE command, so
 * every rule about linking an account and admitting an attendee still applies.
 */

/** Same bounds as the rules and the backend: three characters, at most 100. */
export const MIN_LOOKUP_LENGTH = 3;
export const MAX_LOOKUP_LENGTH = 100;
/** Wait for the typing to pause before spending one of the organiser's searches. */
export const LOOKUP_DEBOUNCE_MS = 400;
/** A cold start is a few seconds; past this the answer is not coming. */
export const LOOKUP_TIMEOUT_MS = 20_000;
/**
 * When a request no trigger ever answers is removed by the database's TTL
 * policy, query and all. The trigger replaces it as soon as it picks the
 * request up. The rules take one to 48 hours from the write, so a day leaves
 * room for this browser's clock being off by most of a day either way.
 */
export const LOOKUP_REQUEST_EXPIRY_MS = 24 * 60 * 60 * 1000;

export const lookupPersonSchema = z.object({
  id: z.string(),
  name: z.string(),
  company: z.string(),
  email: z.string(),
});
export type LookupPerson = z.infer<typeof lookupPersonSchema>;

const lookupAnswerSchema = z.object({
  status: z.string(),
  error: z.string().optional(),
  results: z.array(lookupPersonSchema).optional(),
});

export type AttendeeLookupState =
  /** Nothing typed yet, or fewer than three characters. */
  | { status: "idle" }
  | { status: "searching" }
  | { status: "done"; people: LookupPerson[] }
  | { status: "error"; message: string };

export const LOOKUP_START_FAILED =
  "The search could not be started. Check your connection and try again.";
export const LOOKUP_TIMED_OUT = "The search took too long. Try again.";
export const LOOKUP_UNREADABLE =
  "The search gave an answer that could not be read.";

/** Removes a request the picker is done with. A failure is not worth showing:
 *  the next search removes the earlier ones on the server as well. */
function removeQuietly(ref: DocumentReference): void {
  void deleteDoc(ref).catch(() => undefined);
}

/**
 * Searches while `query` has at least three characters, after a pause.
 *
 * Each change of the query cancels the one before it: its timer, its listener
 * and its answer. A response that arrives for a query the person has typed
 * past is never shown, because the listener that would show it is gone.
 */
export function useAttendeeLookup(
  eventId: string,
  uid: string,
  query: string,
  options: { debounceMs?: number; timeoutMs?: number } = {},
) {
  const { debounceMs = LOOKUP_DEBOUNCE_MS, timeoutMs = LOOKUP_TIMEOUT_MS } =
    options;
  const text = query.trim();
  const [state, setState] = useState<AttendeeLookupState>({ status: "idle" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (text.length < MIN_LOOKUP_LENGTH || text.length > MAX_LOOKUP_LENGTH) {
      setState({ status: "idle" });
      return;
    }
    setState({ status: "searching" });

    let cancelled = false;
    let created = false;
    let unsubscribe: (() => void) | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const request = doc(collection(db, "events", eventId, "attendee_lookups"));

    const settle = (next: AttendeeLookupState) => {
      if (cancelled) return;
      clearTimeout(timeout);
      unsubscribe?.();
      unsubscribe = undefined;
      // The answer is in hand; nothing needs the request any more.
      if (created) removeQuietly(request);
      created = false;
      setState(next);
    };

    const timer = setTimeout(() => {
      setDoc(request, {
        requestedBy: uid,
        requestedAt: serverTimestamp(),
        status: "REQUESTED",
        query: text,
        expiresAt: Timestamp.fromMillis(Date.now() + LOOKUP_REQUEST_EXPIRY_MS),
      }).then(
        () => {
          created = true;
          if (cancelled) {
            created = false;
            removeQuietly(request);
            return;
          }
          timeout = setTimeout(
            () => settle({ status: "error", message: LOOKUP_TIMED_OUT }),
            timeoutMs,
          );
          unsubscribe = onSnapshot(
            request,
            (snapshot) => {
              if (!snapshot.exists()) return;
              const answer = lookupAnswerSchema.safeParse(snapshot.data());
              if (!answer.success) {
                settle({ status: "error", message: LOOKUP_UNREADABLE });
              } else if (answer.data.status === "SUCCEEDED") {
                settle({ status: "done", people: answer.data.results ?? [] });
              } else if (answer.data.status === "FAILED") {
                settle({
                  status: "error",
                  message: answer.data.error ?? LOOKUP_UNREADABLE,
                });
              }
            },
            () => settle({ status: "error", message: LOOKUP_START_FAILED }),
          );
        },
        () => settle({ status: "error", message: LOOKUP_START_FAILED }),
      );
    }, debounceMs);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      clearTimeout(timeout);
      unsubscribe?.();
      if (created) removeQuietly(request);
    };
  }, [eventId, uid, text, attempt, debounceMs, timeoutMs]);

  const retry = useCallback(() => setAttempt((count) => count + 1), []);
  return { state, retry };
}
